import { ECompareVMStatus, EMergeVMStatus, IEntitiesContainerVM, IPropertyValueVM, IGroupConfigurationComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import { IGroupConfigurationData } from 'model/FlexibleAttributeModel';

export type TMergeActionMap = Record<string, EMergeVMStatus>;

interface IEntityLike {
    id?: string;
    name?: string;
    fields?: Array<{ id?: string; name?: string; masterAttributeID?: string }>;
    nonStandards?: IEntityLike[];
    attributeSets?: IEntityLike[];
    product?: { id?: string; name?: string } | null;
}

interface INestedMergeVM {
    id: string;
    status: ECompareVMStatus;
    mergeAction?: EMergeVMStatus;
    attributes: IPropertyValueVM[];
}

interface IListResolver {
    selector: (json: IGroupConfigurationData) => IEntityLike[];
    update: (json: IGroupConfigurationData, items: IEntityLike[]) => void;
    entitiesVM: IEntitiesContainerVM;
}

/**
 * Builds merged group-configuration JSON from the comparison view model.
 */
export class GroupConfigurationMergeBuilder {
    private static cloneDeep<T>(value: T): T {
        return structuredClone(value);
    }

    private static createEntityMap(entities: IEntityLike[]): Map<string, IEntityLike> {
        const map = new Map<string, IEntityLike>();

        entities.forEach((entity) => {
            if (entity.id)
                map.set(entity.id, entity);
        });

        return map;
    }

    private static validateEntityForMerge(entity: IEntityLike | undefined): boolean {
        if (!entity)
            return false;

        // Validate entity has required id
        if (!entity.id)
            return false;

        // Validate entity has name (for non-technical entities)
        if (!entity.name)
            return false;

        // Validate tier-related fields if present
        const tierId = (entity as any)?.tierId;
        const tierCode = (entity as any)?.tierCode;

        if ((tierId !== undefined && !tierId) || (tierCode !== undefined && !tierCode))
            return false;

        // Validate fields array if present
        if (entity.fields && !Array.isArray(entity.fields))
            return false;

        return true;
    }

    private static createListMutator(source: IEntityLike[]) {
        const list = this.cloneDeep(source);
        const indexById = new Map<string, number>();

        const rebuildIndex = () => {
            indexById.clear();
            list.forEach((entity, index) => {
                if (entity.id)
                    indexById.set(entity.id, index);
            });
        };

        const clone = <T,>(value: T): T => this.cloneDeep(value);

        rebuildIndex();

        return {
            list,
            getById: (id?: string): IEntityLike | undefined => {
                if (!id)
                    return undefined;

                const index = indexById.get(id);
                return index === undefined ? undefined : list[index];
            },
            addIfMissing: (entity: IEntityLike) => {
                if (!entity.id || indexById.has(entity.id))
                    return;

                if (!this.validateEntityForMerge(entity))
                    return;

                list.push(clone(entity));
                rebuildIndex();
            },
            replaceOrAdd: (entity: IEntityLike) => {
                if (!entity.id) {
                    list.push(clone(entity));
                    rebuildIndex();
                    return;
                }

                if (!this.validateEntityForMerge(entity))
                    return;

                const index = indexById.get(entity.id);
                if (index === undefined)
                    list.push(clone(entity));
                else
                    list.splice(index, 1, clone(entity));

                rebuildIndex();
            },
            removeById: (id?: string) => {
                if (!id)
                    return;

                const index = indexById.get(id);
                if (index === undefined)
                    return;

                list.splice(index, 1);
                rebuildIndex();
            },
        };
    }

    private static findFieldIndexByAttributeId(
        fields: Array<{ id?: string; masterAttributeID?: string; name?: string }>,
        attributeId: string,
    ): number {
        return fields.findIndex((field) => field.masterAttributeID === attributeId || field.id === attributeId);
    }

    private static getFieldAttributeIds(fieldVM: IPropertyValueVM): string[] {
        const ids = [
            fieldVM.attributeDefinition?.bcbs_config_attribute?._bcbs_config_masterattributeid_value,
            fieldVM.attributeDefinition?.bcbs_config_attribute?.bcbs_config_attributeid,
        ].filter((value): value is string => !!value);

        return Array.from(new Set(ids));
    }

    private static findFieldIndexByAttributeIds(
        fields: Array<{ id?: string; masterAttributeID?: string; name?: string }>,
        attributeIds: string[],
    ): number {
        for (const attributeId of attributeIds) {
            const fieldIndex = this.findFieldIndexByAttributeId(fields, attributeId);
            if (fieldIndex >= 0)
                return fieldIndex;
        }

        return -1;
    }

    /**
     * Applies field-level merge selections onto a cloned entity.
     */
    private static applyFieldMergeActions(
        mergedEntity: IEntityLike,
        originEntity: IEntityLike | undefined,
        fieldsVM: IPropertyValueVM[],
    ) {
        if (!fieldsVM?.length)
            return;

        const mergedFields = this.cloneDeep(mergedEntity.fields ?? []);
        const originFields = originEntity?.fields ?? [];

        fieldsVM.forEach((fieldVM) => {
            if (fieldVM.mergeAction !== EMergeVMStatus.UseOrigin)
                return;

            const fieldAttributeIds = this.getFieldAttributeIds(fieldVM);
            if (!fieldAttributeIds.length)
                return;

            const originFieldIndex = this.findFieldIndexByAttributeIds(originFields, fieldAttributeIds);
            if (originFieldIndex < 0)
                return;

            const originField = originFields[originFieldIndex];
            const mergedFieldIndex = this.findFieldIndexByAttributeIds(mergedFields, fieldAttributeIds);

            if (mergedFieldIndex >= 0)
                mergedFields.splice(mergedFieldIndex, 1, this.cloneDeep(originField));
            else
                mergedFields.push(this.cloneDeep(originField));
        });

        mergedEntity.fields = mergedFields;
    }

    /**
     * Applies merge actions to nested attribute-set or non-standard collections.
     */
    private static applyNestedCollectionMerge(
        sourceOrigin: IEntityLike[],
        sourceTarget: IEntityLike[],
        mergeVMs: INestedMergeVM[],
    ): IEntityLike[] {
        const originById = this.createEntityMap(sourceOrigin);
        const targetById = this.createEntityMap(sourceTarget);
        const mutator = this.createListMutator(sourceTarget);

        mergeVMs.forEach((mergeVM) => {
            const mergeAction = mergeVM.mergeAction ?? EMergeVMStatus.UseTarget;

             // Skip actions that are not meant to change the target JSON.
            if (mergeAction === EMergeVMStatus.UseTarget || mergeAction === EMergeVMStatus.Disabled)
                return;

            const originEntity = originById.get(mergeVM.id);
            const targetEntity = targetById.get(mergeVM.id);

            if (mergeVM.status === ECompareVMStatus.OnOriginOnly) {
                if (mergeAction === EMergeVMStatus.AddOrigin && originEntity)
                    mutator.addIfMissing(originEntity);
            } else if (mergeVM.status === ECompareVMStatus.OnTargetOnly) {
                if (mergeAction === EMergeVMStatus.RemoveTarget)
                    mutator.removeById(targetEntity?.id ?? mergeVM.id);
            } else if (mergeVM.status === ECompareVMStatus.Modified) {
                if (mergeAction === EMergeVMStatus.UseOrigin && originEntity) {
                    mutator.replaceOrAdd(originEntity);
                } else if (mergeAction === EMergeVMStatus.Merge) {
                    const baselineEntity = targetEntity ?? originEntity;
                    if (baselineEntity) {
                        const mergedEntity = this.cloneDeep(mutator.getById(baselineEntity.id) ?? baselineEntity);
                        this.applyFieldMergeActions(mergedEntity, originEntity, mergeVM.attributes ?? []);
                        mutator.replaceOrAdd(mergedEntity);
                    }
                }
            }
        });

        return mutator.list;
    }

    /**
     * Collects nested merge view models from tabs and sections.
     */
    private static collectNestedMergeVMs(containerEntity: IEntitiesContainerVM['entities'][number]) {
        const attributeSetVMs: INestedMergeVM[] = [];
        const nonStandardVMs: INestedMergeVM[] = [];

        containerEntity.tabsVM.forEach((tabVM) => {
            tabVM.sectionsVM.forEach((sectionVM) => {
                sectionVM.attributeSetsVM.forEach((attributeSetGroupVM) => {
                    attributeSetGroupVM.attributeSets.forEach((attributeSetVM) => {
                        attributeSetVMs.push({
                            id: attributeSetVM.id,
                            status: attributeSetVM.compareResult,
                            mergeAction: attributeSetVM.mergeAction,
                            attributes: attributeSetVM.attributesVM as any,
                        });
                    });
                });

                sectionVM.nonStandardsVM.forEach((nonStandardVM) => {
                    nonStandardVMs.push({
                        id: nonStandardVM.id,
                        status: nonStandardVM.compareResult,
                        mergeAction: nonStandardVM.mergeAction,
                        attributes: nonStandardVM.attributesVM as any,
                    });
                });
            });
        });

        return {
            attributeSetVMs,
            nonStandardVMs,
        };
    }

    /**
     * Applies nested merges to attribute sets and non-standard sections.
     */
    private static applyNestedEntityMerge(
        entityVM: IEntitiesContainerVM['entities'][number],
        originEntity: IEntityLike | undefined,
        targetEntity: IEntityLike | undefined,
        mergedEntity: IEntityLike,
    ) {
        const originWithNested = originEntity as IEntityLike | undefined;
        const targetWithNested = targetEntity as IEntityLike | undefined;
        const mergedWithNested = mergedEntity as IEntityLike;

        const { attributeSetVMs, nonStandardVMs } = this.collectNestedMergeVMs(entityVM);

        if ((originWithNested?.attributeSets || targetWithNested?.attributeSets) && attributeSetVMs.length > 0) {
            const originAttributeSets = (originWithNested?.attributeSets ?? []);
            const targetAttributeSets = (targetWithNested?.attributeSets ?? []);
            mergedWithNested.attributeSets = this.applyNestedCollectionMerge(originAttributeSets, targetAttributeSets, attributeSetVMs);
        }

        if ((originWithNested?.nonStandards || targetWithNested?.nonStandards) && nonStandardVMs.length > 0) {
            const originNonStandards = (originWithNested?.nonStandards ?? []);
            const targetNonStandards = (targetWithNested?.nonStandards ?? []);
            mergedWithNested.nonStandards = this.applyNestedCollectionMerge(originNonStandards, targetNonStandards, nonStandardVMs);
        }
    }

    /**
     * Collects row-level field nodes from a comparison entity.
     */
    private static collectEntityFieldRows(entityVM: IEntitiesContainerVM['entities'][number]): IPropertyValueVM[] {
        const fieldRows: IPropertyValueVM[] = [];

        entityVM.tabsVM.forEach((tabVM) => {
            tabVM.sectionsVM.forEach((sectionVM) => {
                sectionVM.attributesVM.forEach((fieldRow) => {
                    fieldRows.push(fieldRow as any);
                });
            });
        });

        return fieldRows;
    }

    /**
     * Applies the merge decision for each entity in a container.
     */
    private static applyModel(
        resolver: IListResolver,
        targetJson: IGroupConfigurationData,
    ) {
        const mutator = this.createListMutator(resolver.selector(targetJson));

        resolver.entitiesVM.entities.forEach((entityVM) => {
            const mergeAction = entityVM.mergeAction ?? EMergeVMStatus.UseTarget;

            // Skip actions that are not meant to change the target JSON.
            if (mergeAction === EMergeVMStatus.UseTarget || mergeAction === EMergeVMStatus.Disabled)
                return;

            if (entityVM.compareResult === undefined)
                return;

            const originEntity = entityVM.entityCompared.onOrigin as IEntityLike | undefined;
            const targetEntity = entityVM.entityCompared.onTarget as IEntityLike | undefined;

            const statusHandlers: Partial<Record<ECompareVMStatus, () => void>> = {
                [ECompareVMStatus.OnOriginOnly]: () => {
                    if (mergeAction === EMergeVMStatus.AddOrigin && originEntity)
                        mutator.addIfMissing(originEntity);
                },
                [ECompareVMStatus.OnTargetOnly]: () => {
                    if (mergeAction === EMergeVMStatus.RemoveTarget)
                        mutator.removeById(targetEntity?.id);
                },
                [ECompareVMStatus.Modified]: () => {
                    if (mergeAction === EMergeVMStatus.UseOrigin && originEntity) {
                        mutator.replaceOrAdd(originEntity);
                        return;
                    }

                    if (mergeAction !== EMergeVMStatus.Merge)
                        return;

                    const baselineEntity = targetEntity ?? originEntity;
                    if (!baselineEntity)
                        return;

                    const mergedEntity = this.cloneDeep(mutator.getById(baselineEntity.id) ?? baselineEntity);

                    this.applyNestedEntityMerge(entityVM, originEntity, targetEntity, mergedEntity);
                    this.applyFieldMergeActions(mergedEntity, originEntity, this.collectEntityFieldRows(entityVM));
                    mutator.replaceOrAdd(mergedEntity);
                },
            };

            statusHandlers[entityVM.compareResult]?.();
        });

        resolver.update(targetJson, mutator.list);
    }

    /**
     * Builds a merged snapshot JSON object from the comparison model.
     */
    public static buildMergedSnapshotJson(
        target: IGroupConfigurationData,
        viewModel: IGroupConfigurationComparisonVM,
    ): IGroupConfigurationData {

        const targetJsonClone = this.cloneDeep(target);

        // Scope Methods
        // -----------------------------------------------------
        // This wrapper keeps each section merge aligned with the comparison model:
        // each list is selected, applied, and written back through the same path.
        const applyListMerge = (
            entitiesVM: IEntitiesContainerVM,
            selector: (json: IGroupConfigurationData) => IEntityLike[],
            updater: (json: IGroupConfigurationData, items: IEntityLike[]) => void,
        ): void => {
            this.applyModel({ entitiesVM, selector, update: updater }, targetJsonClone);
        };

        // group configuration
        // -----------------------------------------------------
        // The top-level snapshot object is merged as a single synthetic entity.
        applyListMerge(
            viewModel.groupConfiguration,
            (json) => [json as unknown as IEntityLike],
            (json, items) => {
                const merged = (items[0] ?? {}) as Partial<IGroupConfigurationData>;
                Object.assign(json, merged);
            },
        );

        // plans
        // -----------------------------------------------------
        applyListMerge(
            viewModel.plan,
            (json) => (json.plans ?? []),
            (json, items) => {
                json.plans = items as any;
            },
        );

        // Benefit
        // -----------------------------------------------------
        applyListMerge(
            viewModel.benefit,
            (json) => (json.benefits ?? []),
            (json, items) => {
                json.benefits = items as any;
            },
        );

        // Group Numbers
        // -----------------------------------------------------
        // These three collections live under the shared groupStructure container,
        // so each update ensures the nested object exists before assigning items.
        applyListMerge(
            viewModel.groupNumber,
            (json) => (json.groupStructure?.groupNumbers ?? []),
            (json, items) => {
                json.groupStructure = json.groupStructure ?? { groupNumbers: [], cesNumbers: [], departments: [] } as any;
                json.groupStructure.groupNumbers = items as any;
            },
        );

        // CES Numbers
        // -----------------------------------------------------
        applyListMerge(
            viewModel.cesNumber,
            (json) => (json.groupStructure?.cesNumbers ?? []),
            (json, items) => {
                json.groupStructure = json.groupStructure ?? { groupNumbers: [], cesNumbers: [], departments: [] } as any;
                json.groupStructure.cesNumbers = items as any;
            },
        );

        // Departments
        // -----------------------------------------------------
        applyListMerge(
            viewModel.department,
            (json) => (json.groupStructure?.departments ?? []) as IEntityLike[],
            (json, items) => {
                json.groupStructure = json.groupStructure ?? { groupNumbers: [], cesNumbers: [], departments: [] } as any;
                json.groupStructure.departments = items as any;
            },
        );

        // Fees and Premiums
        // -----------------------------------------------------
        applyListMerge(
            viewModel.feesAndPremium,
            (json) => (json.feesAndPremiums ?? []),
            (json, items) => {
                json.feesAndPremiums = items as any;
            },
        );

        // Fees Summary
        // -----------------------------------------------------
        // Fees summary is derived from fees and premiums, so it stays comparison-only.
        // Fees summary is a computed rollup of fees and premium records and has no independent
        // backing collection in IGroupConfigurationData, so merging it directly would be
        // redundant and dangerous. All entities are forced to EUnsupportedMergeAction by
        // GenerateGroupConfigurationModel and are intentionally skipped here.

        // Stop Loss Summary
        // -----------------------------------------------------
        // Stop loss summary follows the same rule as fees summary: it is not a
        // persisted collection, only a derived view of stop loss records.
        // Stop loss summary is a computed rollup of stop loss records and has no independent
        // backing collection in IGroupConfigurationData. All entities are forced to
        // EUnsupportedMergeAction by GenerateGroupConfigurationModel and are intentionally skipped here.

        // Stop Loss
        // -----------------------------------------------------
        applyListMerge(
            viewModel.stopLoss,
            (json) => (json.stopLoss ?? []),
            (json, items) => {
                json.stopLoss = items as any;
            },
        );

        // Accounts Receivable
        // -----------------------------------------------------
        applyListMerge(
            viewModel.accountsReceivable,
            (json) => (json.claimsARs ?? []),
            (json, items) => {
                json.claimsARs = items as any;
            },
        );

        // Non-Standard Templates
        // -----------------------------------------------------
        applyListMerge(
            viewModel.nonStandardTemplate,
            (json) => (json.nonStandardTemplates ?? []) as IEntityLike[],
            (json, items) => {
                json.nonStandardTemplates = items as any;
            },
        );

        return targetJsonClone;
    }
}

export const buildMergedSnapshotJson = (
    target: IGroupConfigurationData,
    model: IGroupConfigurationComparisonVM,
): IGroupConfigurationData => {
    return GroupConfigurationMergeBuilder.buildMergedSnapshotJson(target, model);
};
